import { Node, mergeAttributes } from "@tiptap/core";
import mermaid from "mermaid";

// Renders diagrams (flowcharts, sequence/state diagrams, …) from Mermaid source text.
// Mirrors MathField's shape: only the source text is persisted (data-mermaid-source), and
// the actual SVG is rendered client-side on hydration, both here (live editor NodeView) and
// in RichContentDisplay's read-only hydrateStatic() below — never round-tripped through the
// sanitizer, which only ever needs to know about the container div and its data attribute
// (see QuestionContentRenderer.cs's AllowedAttributes).
let mermaidReady = false;
function ensureMermaid(): void {
  if (mermaidReady) return;
  mermaid.initialize({ startOnLoad: false, securityLevel: "strict", theme: "neutral" });
  mermaidReady = true;
}

let renderSeq = 0;

// A diagram can fail to render for two very different readers. In the editor, the author
// wrote the source and the parser's complaint is the only way to fix it, so it is shown.
// Everywhere else — a suggestion card, a question on an exam — the source came from the
// model and the reader can do nothing with "Parse error on line 4", so a diagram that will
// not build is dropped and the rest of the question stands on its own. The source text stays
// in the attribute either way, so nothing is lost and opening the editor still shows why.
export async function renderMermaidDiagram(
  host: HTMLElement,
  source: string,
  opts?: { silent?: boolean },
): Promise<void> {
  ensureMermaid();
  const trimmed = (source || "").trim();
  if (!trimmed) {
    host.replaceChildren();
    host.classList.remove("has-error");
    return;
  }
  const id = `mermaid-diagram-render-${Date.now()}-${renderSeq++}`;
  try {
    const { svg } = await mermaid.render(id, trimmed);
    host.innerHTML = svg;
    host.classList.remove("has-error");
    host.classList.remove("is-hidden");
  } catch (err) {
    if (opts?.silent) {
      host.replaceChildren();
      host.classList.remove("has-error");
      host.classList.add("is-hidden");
      console.warn("mermaid: diagram dropped", err);
    } else {
      host.textContent = (err as Error)?.message || "Invalid diagram";
      host.classList.add("has-error");
    }
    // mermaid.render() leaves a detached error/temp node behind in the DOM on failure
    // (its own cleanup only runs on success) — remove it so failed attempts don't pile up.
    document.getElementById(id)?.remove();
  }
}

export const MermaidDiagram = Node.create({
  name: "mermaidDiagram",
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      source: {
        default: "",
        parseHTML: (el: HTMLElement) => el.getAttribute("data-mermaid-source") || "",
        renderHTML: (attrs: { source: string }) => ({ "data-mermaid-source": attrs.source }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div.mermaid-diagram-block[data-mermaid-source]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { class: "mermaid-diagram-block", contenteditable: "false" })];
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement("div");
      dom.className = "mermaid-diagram-block";
      dom.setAttribute("data-mermaid-source", node.attrs.source);
      dom.setAttribute("contenteditable", "false");
      renderMermaidDiagram(dom, node.attrs.source);

      return {
        dom,
        selectNode: () => dom.classList.add("is-selected"),
        deselectNode: () => dom.classList.remove("is-selected"),
        update: (updatedNode) => {
          if (updatedNode.type.name !== "mermaidDiagram") return false;
          if (updatedNode.attrs.source !== dom.getAttribute("data-mermaid-source")) {
            dom.setAttribute("data-mermaid-source", updatedNode.attrs.source);
            renderMermaidDiagram(dom, updatedNode.attrs.source);
          }
          return true;
        },
      };
    };
  },
});
